import { DataRef } from './DataRef';
import { IFeature } from '../Feature/FeatureModel';
import { IRootStore } from '../Root/RootStore';
import { ISurvey } from '../Survey/SurveyModel';
import { Instance, cast, getRoot, types } from 'mobx-state-tree';
import { MetadataModel } from '../Question/MetadataModel';

export const TreeNode = types
  .model('TreeNode', {
    id: types.identifier,
    key: types.string,
    dataRef: types.maybeNull(DataRef),
    namespace: types.maybeNull(types.string),
    disableStepBack: types.maybeNull(types.boolean),
    meta: types.maybeNull(types.array(MetadataModel)),
  })
  .actions(self => ({
    addOrUpdateMeta(key: string, value: string) {
      // Initialize meta array if it doesn't exist
      if (!self.meta) {
        self.meta = cast([]);
      }

      // Check if metadata with this key already exists
      const existingMetaIndex = self.meta!.findIndex(m => m.key === key);
      if (existingMetaIndex !== -1) {
        // Update existing metadata
        self.meta![existingMetaIndex].value = value;
      } else {
        // Add new metadata
        self.meta!.push({ key, value });
      }
    }
  }))
  .views(self => ({
    get isContentReady(): boolean {
      if (!self.dataRef) {
        return false;
      }
      if (self.dataRef.type === 'FEATURE') {
        return !!this.feature;
      }
      return !!self.dataRef?.maybeData;
    },
    getMetaValue(metaKey: string) {
      const meta = self.meta?.find(m => m.key === metaKey);
      return meta ? meta.value : undefined;
    },
    get isLastInSurvey(): boolean {
      return !!self.meta?.some(m => m.key === 'isLastInSurvey');
    },
    get modifiesNextNode(): boolean {
      return !!self.meta?.some(m => m.key == 'modifiesTree' && m.value === 'next');
    },
    get modifiesNodesAfterNext(): boolean {
      return !!self.meta?.some(m => m.key == 'modifiesTree' && m.value === 'afterNext');
    },
    get modifiesTree(): boolean {
      return !!self.meta?.some(m => m.key === 'modifiesTree');
    },
    get surveyId(): string | undefined {
      return self.meta?.find(m => m.key === 'survey')?.value;
    },
    get survey(): ISurvey | undefined {
      const surveyId = this.surveyId;
      if (surveyId) {
        return getRoot<IRootStore>(self).survey.getSurvey(surveyId) || undefined;
      }
    },
    get sessionStepId(): string | undefined {
      return self.meta?.find(m => m.key === 'sessionStepId')?.value;
    },
    /** the key of the question this node asks (block rules are written with question keys) */
    get questionKey(): string | undefined {
      return self.meta?.find(m => m.key === 'questionKey')?.value;
    },
    get feature(): IFeature | undefined {
      return self.dataRef?.feature;
    }
  }));

export type ITreeNode = Instance<typeof TreeNode>;
